import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';

@Component({
    selector: 'add-audience',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, InputComponent, FormsModule],
    templateUrl: './add-audience.component.html',
    styleUrls: ['./add-audience.component.scss']
})
export class AddAudienceComponent implements OnInit, OnDestroy {
    @Input() initialData: any = null;
    @Output() formSubmitted = new EventEmitter<any>();
    @Output() formValidation = new EventEmitter<boolean>();

    form!: FormGroup;
    private dialogConfirmListener?: (event: Event) => void;

    customErrorMessages = {
        audienceTypeName: { required: 'Please enter a audience name.' },
    };

    constructor(private fb: FormBuilder) { }

    ngOnInit(): void {
        this.initForm();
        this.setupDialogListener();
    }

    ngOnDestroy(): void {
        this.cleanupDialogListener();
    }

    private initForm(): void {
        const initialStatus = this.initialData
            ? this.initialData.status === 'Active'
            : true;

        this.form = this.fb.group({
            audienceTypeName: [this.initialData ? this.initialData['audience name'] ?? this.initialData.audienceName?.trim() ?? '' : '', [requiredTrimmed]],
            audienceDescription: [
                this.initialData?.description || '',
                [this.noWhitespaceOnlyValidator.bind(this)]
            ],
            status: [initialStatus]
        });

        this.formValidation.emit(this.form.valid);
        this.form.valueChanges.subscribe(() => {
            this.formValidation.emit(this.form.valid);
        });
    }


    private noWhitespaceOnlyValidator(control: AbstractControl): { [key: string]: any } | null {
        if (control.value === null || control.value === undefined || control.value === '') {
            return null;
        }

        if (control.value.trim().length === 0) {
            return { whitespaceOnly: true };
        }
        return null;
    }

    private setupDialogListener(): void {
        this.dialogConfirmListener = () => this.handleDialogConfirm();
        document.addEventListener('dialogConfirm', this.dialogConfirmListener);
    }

    private cleanupDialogListener(): void {
        if (this.dialogConfirmListener) {
            document.removeEventListener('dialogConfirm', this.dialogConfirmListener);
        }
    }

    handleDialogConfirm(): void {
        if (this.form.valid) {
            const formValue = this.form.value;
            if (formValue.audienceDescription) {
                formValue.audienceDescription = formValue.audienceDescription.trim();
            }
            this.formSubmitted.emit(formValue);
        } else {
            this.markFormAsTouched();
        }
    }

    onSubmit(): void {
        this.handleDialogConfirm();
    }

    private markFormAsTouched(): void {
        this.form.markAllAsTouched();
    }

    isFormValid(): boolean {
        return this.form.valid;
    }

    autoResize(event: Event) {
        const textarea = event.target as HTMLTextAreaElement;
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    }

    hasDescriptionError(): boolean {
        const descriptionControl = this.form.get('audienceDescription');
        return descriptionControl ? (descriptionControl.invalid && descriptionControl.touched) : false;
    }

    getDescriptionError(): string {
        const descriptionControl = this.form.get('audienceDescription');
        if (descriptionControl && descriptionControl.errors && descriptionControl.touched) {
            if (descriptionControl.errors['whitespaceOnly']) {
                return 'Please enter a valid description.';
            }
        }
        return '';
    }
}